import { motion, useScroll, useTransform } from "framer-motion";
import { useRef } from "react";
import { BlobField } from "./BlobField";
import { HERO } from "@/constants/testIds";

const line = {
  hidden: { y: "115%" },
  show: (i = 0) => ({
    y: "0%",
    transition: { duration: 1.05, delay: 0.2 + i * 0.09, ease: [0.22, 0.61, 0.36, 1] },
  }),
};

const HEADLINE = [
  ["We", "engineer", "digital"],
  ["products", "that", "feel", "alive."],
];

export const Hero = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const yLead = useTransform(scrollYProgress, [0, 1], [0, -60]);
  const opacityLead = useTransform(scrollYProgress, [0, 0.8], [1, 0]);

  return (
    <section
      id="top"
      ref={ref}
      data-testid={HERO.section}
      className="relative min-h-[100svh] w-full overflow-hidden pt-32 pb-24 md:pt-40 md:pb-32"
    >
      <BlobField variant="hero" />
      <div className="grain absolute inset-0 -z-10" />

      <div className="mx-auto max-w-[1400px] px-6 md:px-10">
        {/* Top eyebrow */}
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.05 }}
          className="flex items-center gap-3 text-[11px] uppercase tracking-[0.28em] text-emerald-300/80 mb-10"
        >
          <span className="inline-block w-1.5 h-1.5 rounded-full bg-emerald-400 shadow-[0_0_16px_#00D084]" />
          Dove Infotech Pvt. Ltd. — Estd. 2005
        </motion.div>

        {/* Kinetic masked headline */}
        <motion.h1
          data-testid={HERO.headline}
          style={{ y: yLead, opacity: opacityLead }}
          className="font-display font-black text-white tracking-tighter leading-[0.92] text-[13vw] md:text-[9.4vw] lg:text-[8.2vw]"
        >
          {HEADLINE.map((row, rIdx) => (
            <span key={rIdx} className="reveal-line-wrap block">
              <motion.span
                initial="hidden"
                animate="show"
                custom={rIdx}
                variants={line}
                className="block will-change-transform"
              >
                {row.map((w, wi) => (
                  <span key={wi} className="inline-block mr-[0.22em]">
                    {w === "alive." ? (
                      <span className="italic font-normal text-emerald-300/95">{w}</span>
                    ) : (
                      w
                    )}
                  </span>
                ))}
              </motion.span>
            </span>
          ))}
        </motion.h1>

        {/* Sub row */}
        <div className="mt-14 md:mt-20 grid grid-cols-1 md:grid-cols-12 gap-10 items-end">
          <motion.p
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, delay: 1.15, ease: [0.22, 0.61, 0.36, 1] }}
            className="md:col-span-6 md:col-start-1 text-white/70 text-base md:text-lg leading-relaxed max-w-[54ch]"
          >
            For two decades we have been designing, engineering and shipping
            software for teams who refuse the ordinary — web apps, mobile
            products, immersive UX, cinematic edits, and cloud platforms that
            scale with quiet confidence.
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, delay: 1.3 }}
            className="md:col-span-5 md:col-start-8 flex flex-col sm:flex-row gap-4"
          >
            <a
              href="#contact"
              data-testid={HERO.ctaPrimary}
              className="group inline-flex items-center justify-between gap-4 rounded-full bg-emerald-400 text-emerald-950 pl-6 pr-2 py-2 hover:bg-emerald-300 transition-colors"
            >
              <span className="text-sm font-medium tracking-wide">Start a project</span>
              <span className="grid place-items-center w-10 h-10 rounded-full bg-emerald-950 text-emerald-300 group-hover:translate-x-1 transition-transform">
                →
              </span>
            </a>
            <a
              href="#work"
              data-testid={HERO.ctaSecondary}
              className="inline-flex items-center justify-center gap-3 rounded-full border border-white/15 text-white/85 px-6 py-3 text-sm hover:border-emerald-400/60 hover:text-white transition-colors"
            >
              See selected work
              <span aria-hidden>↘</span>
            </a>
          </motion.div>
        </div>

        {/* Bottom side info */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 1, delay: 1.7 }}
          className="mt-20 md:mt-28 grid grid-cols-2 md:grid-cols-4 gap-8 text-[11px] uppercase tracking-[0.22em] text-white/40"
        >
          <div>
            <div className="text-white/85 font-display text-2xl md:text-3xl mb-1">20+</div>
            years of craft
          </div>
          <div>
            <div className="text-white/85 font-display text-2xl md:text-3xl mb-1">140+</div>
            products shipped
          </div>
          <div>
            <div className="text-white/85 font-display text-2xl md:text-3xl mb-1">32</div>
            countries served
          </div>
          <div>
            <div className="text-white/85 font-display text-2xl md:text-3xl mb-1">4.9</div>
            avg client rating
          </div>
        </motion.div>
      </div>
    </section>
  );
};
